import {Injectable} from '@angular/core';
import * as Levenshtein from 'fastest-levenshtein';
import {Product} from '../../types/models';
import {ThermomixItem, ThermomixMatch} from '../../types/thermomix';
import {SearchService} from './search.service';
import {ProductService} from './product.service';

/**
 * Ordnet Eintraege der Thermomix-Einkaufsliste Kiwi-Produkten zu.
 *
 * Der Score ist das Maximum aus zwei Sichtweisen, jeweils gegen Name und Kurzname des Produkts:
 * der Aehnlichkeit der ganzen Bezeichnung (Levenshtein) und der Abdeckung der einzelnen Woerter
 * der Zutat im Produktnamen. Nur bei MIN_AUTO_SCORE oder mehr wird automatisch zugeordnet,
 * darunter entscheidet der Kunde - "Zucker" soll nicht stillschweigend zu "Puderzucker" werden.
 */
@Injectable({
  providedIn: 'root'
})
export class ThermomixMatchService {

  /** Ab dieser Uebereinstimmung in Prozent wird ohne Rueckfrage zugeordnet. */
  static readonly MIN_AUTO_SCORE = 90;

  /** So viele Treffer werden dem Kunden zur Auswahl angeboten. */
  static readonly MAX_MATCHES = 12;

  /** Kombinierende Akzentzeichen, die nach normalize('NFD') uebrig bleiben, z.B. in 'Crème'. */
  private static readonly DIACRITICS = new RegExp('[\\u0300-\\u036f]', 'g');

  /** Umrechnung in die Basiseinheit der Kiwi-Produkte (g bzw. ml). */
  private static readonly UNIT_FACTORS: { [unit: string]: { factor: number, base: string } } = {
    g: { factor: 1, base: 'g' },
    kg: { factor: 1000, base: 'g' },
    mg: { factor: 0.001, base: 'g' },
    ml: { factor: 1, base: 'ml' },
    cl: { factor: 10, base: 'ml' },
    dl: { factor: 100, base: 'ml' },
    l: { factor: 1000, base: 'ml' },
    el: { factor: 15, base: 'g' },
    tl: { factor: 5, base: 'g' },
    msp: { factor: 0.5, base: 'g' },
    prise: { factor: 0.5, base: 'g' },
    stk: { factor: 1, base: 'Stk' },
    stueck: { factor: 1, base: 'Stk' },
    bund: { factor: 1, base: 'Stk' },
    zehe: { factor: 1, base: 'Stk' },
    zehen: { factor: 1, base: 'Stk' }
  };

  constructor(
    private searchService: SearchService,
    private productService: ProductService
  ) { }

  /**
   * Sucht Kiwi-Produkte zu einem Eintrag der Einkaufsliste, absteigend nach Uebereinstimmung.
   * Bleibt die Suche mit der vollen Bezeichnung leer, wird sie mit einem gekuerzten Begriff
   * wiederholt, z.B. 'Paprika, rot' -> 'Paprika' oder 'Ei(er)' -> 'Ei'.
   */
  async findMatches(item: ThermomixItem): Promise<ThermomixMatch[]> {
    const name = (item?.Name || '').trim();
    if (!name) {
      return [];
    }
    let products = await this.search(name);
    const shortened = this.shorten(name);
    if (products.length === 0 && shortened && shortened !== name) {
      products = await this.search(shortened);
    }
    return this.rank(name, products);
  }

  /** Sucht Produkte zu einem frei eingegebenen Begriff, z.B. aus dem Suchfeld im Auswahldialog. */
  async findProducts(searchTerm: string, ingredientName?: string): Promise<ThermomixMatch[]> {
    const products = await this.search((searchTerm || '').trim());
    return this.rank(ingredientName || searchTerm, products);
  }

  private async search(searchTerm: string): Promise<Product[]> {
    const term = this.toSearchTerm(searchTerm);
    if (!term) {
      return [];
    }
    const result = await this.searchService.getSearchResult(term);
    return result?.Products || [];
  }

  private rank(ingredientName: string, products: Product[]): ThermomixMatch[] {
    return (products || [])
      .filter(product => !!product)
      .map(product => ({ Product: product, Score: this.getScore(ingredientName, product) }))
      .sort((a, b) => this.compareMatches(a, b))
      .slice(0, ThermomixMatchService.MAX_MATCHES);
  }

  /** Bei gleichem Score stehen verfuegbare Produkte vorn. */
  private compareMatches(a: ThermomixMatch, b: ThermomixMatch): number {
    if (b.Score !== a.Score) {
      return b.Score - a.Score;
    }
    const aInStock = this.productService.getProductInStock(a.Product) ? 1 : 0;
    const bInStock = this.productService.getProductInStock(b.Product) ? 1 : 0;
    return bInStock - aInStock;
  }

  /** Uebereinstimmung zwischen Zutat und Produkt in Prozent (0 - 100). */
  getScore(ingredientName: string, product: Product): number {
    const ingredient = this.normalize(ingredientName);
    if (!ingredient || !product) {
      return 0;
    }
    const candidates = [product.Name, product.NameShort].filter(name => !!name);
    let best = 0;
    for (const candidate of candidates) {
      const normalized = this.normalize(candidate);
      best = Math.max(best, this.getSimilarity(ingredient, normalized), this.getCoverage(ingredient, normalized));
    }
    return best;
  }

  /** Reiner Zeichenvergleich der ganzen Bezeichnungen. */
  private getSimilarity(a: string, b: string): number {
    if (!a || !b) {
      return 0;
    }
    if (a === b) {
      return 100;
    }
    const maxLength = Math.max(a.length, b.length);
    const distance = Levenshtein.distance(a, b);
    return Math.round(100 * Math.max(0, 1 - distance / maxLength));
  }

  /**
   * Abdeckung der Zutatenwoerter im Produktnamen, gewichtet nach Wortlaenge: wie gut findet
   * jedes Wort der Zutat ein Gegenstueck im Produktnamen. Zusaetzliche Woerter im Produktnamen
   * daempfen das Ergebnis, damit 'Salz' nicht voll auf 'Salz Pfeffer Muehle' passt.
   */
  private getCoverage(ingredient: string, productName: string): number {
    const ingredientWords = ingredient.split(' ').filter(word => !!word);
    const productWords = productName.split(' ').filter(word => !!word);
    if (ingredientWords.length === 0 || productWords.length === 0) {
      return 0;
    }
    const matchedProductWords = new Set<string>();
    let weightedScore = 0;
    let totalWeight = 0;
    for (const word of ingredientWords) {
      let bestScore = 0;
      let bestWord: string = null;
      for (const productWord of productWords) {
        const score = this.getSimilarity(word, productWord);
        if (score > bestScore) {
          bestScore = score;
          bestWord = productWord;
        }
      }
      if (bestWord && bestScore > 0) {
        matchedProductWords.add(bestWord);
      }
      weightedScore += bestScore * word.length;
      totalWeight += word.length;
    }
    const coverage = weightedScore / totalWeight;
    const productWeight = productWords.reduce((sum, word) => sum + word.length, 0);
    const matchedWeight = productWords
      .filter(word => matchedProductWords.has(word))
      .reduce((sum, word) => sum + word.length, 0);
    const completeness = productWeight === 0 ? 0 : matchedWeight / productWeight;
    return Math.round(coverage * (0.75 + 0.25 * completeness));
  }

  /**
   * Bringt eine Bezeichnung auf eine vergleichbare Form: Kleinschreibung, Umlaute aufgeloest,
   * Mengen- und Sonderzeichen entfernt.
   */
  normalize(name: string): string {
    return (name || '')
      .toLowerCase()
      .replace(/ä/g, 'ae')
      .replace(/ö/g, 'oe')
      .replace(/ü/g, 'ue')
      .replace(/ß/g, 'ss')
      .normalize('NFD')
      .replace(ThermomixMatchService.DIACRITICS, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  /** Schluessel, unter dem die Entscheidung des Kunden zu einer Zutat gespeichert wird. */
  getDecisionKey(item: ThermomixItem): string {
    return this.normalize(item?.Name);
  }

  /**
   * Kuerzt eine Bezeichnung auf ihren Kern, damit auch ein zweiter Suchversuch noch trifft:
   * 'Paprika, rot' -> 'Paprika', 'Ei(er)' -> 'Ei'.
   */
  private shorten(name: string): string {
    const bySeparator = name.split(/[,(]/)[0].trim();
    if (bySeparator && bySeparator.length < name.length) {
      return bySeparator;
    }
    const bySpace = name.split(/\s+/)[0].trim();
    return bySpace && bySpace.length < name.length ? bySpace : name;
  }

  /**
   * Bereitet eine Bezeichnung als Suchbegriff auf. Die Kiwi-Suche nimmt den Begriff im
   * URL-Pfad entgegen, deshalb bleiben nur Buchstaben, Zahlen und Leerzeichen stehen.
   */
  private toSearchTerm(name: string): string {
    return (name || '')
      .replace(/[^a-z0-9äöüÄÖÜßáàâéèêíìîóòôúùûçñ\s-]+/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /**
   * Wie viele Einheiten des Produkts fuer die benoetigte Menge in den Warenkorb gehoeren.
   * Laesst sich die Menge nicht umrechnen - keine Angabe in Cookidoo oder eine unbekannte
   * Einheit - wird ein Stueck angenommen.
   */
  getCartAmount(item: ThermomixItem, product: Product): number {
    const required = this.toBaseAmount(item);
    const packageAmount = this.toProductBaseAmount(product);
    if (!required || !packageAmount || required.base !== packageAmount.base) {
      return 1;
    }
    return Math.max(1, Math.ceil(required.amount / packageAmount.amount));
  }

  /** Rechnet die Menge eines Eintrags in g, ml oder Stueck um. */
  private toBaseAmount(item: ThermomixItem): { amount: number, base: string } {
    const amount = this.parseAmount(item?.Amount);
    if (!amount) {
      return null;
    }
    const unit = this.normalize(item?.Unit);
    if (!unit) {
      // Ohne Einheit meint Cookidoo Stueckzahlen, z.B. '4 Orangen'.
      return { amount, base: 'Stk' };
    }
    const factor = ThermomixMatchService.UNIT_FACTORS[unit];
    return factor ? { amount: amount * factor.factor, base: factor.base } : null;
  }

  /** Rechnet die Packungsgroesse eines Kiwi-Produkts in g, ml oder Stueck um. */
  private toProductBaseAmount(product: Product): { amount: number, base: string } {
    const amount = this.parseAmount(product?.Amount);
    if (!amount) {
      return null;
    }
    const unit = this.normalize(product?.Unit);
    const factor = ThermomixMatchService.UNIT_FACTORS[unit];
    return factor ? { amount: amount * factor.factor, base: factor.base } : null;
  }

  private parseAmount(value: any): number {
    const amount = Number(value);
    return isNaN(amount) || amount <= 0 ? null : amount;
  }

  /** Menge und Einheit eines Eintrags fuer die Anzeige, z.B. '200 g'. */
  getAmountForDisplay(item: ThermomixItem): string {
    if (!item?.Amount) {
      return item?.Unit || '';
    }
    const amount = Math.round(item.Amount * 100) / 100;
    return item.Unit ? `${amount} ${item.Unit}` : String(amount);
  }
}
